import { Play, Pause, SkipBack, SkipForward, Coins, Volume2 } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { fmtTime } from "@/lib/format";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlayerBar() {
  const p = usePlayer();
  if (!p?.current) return null;
  const { current, playing, progress, duration, volume, setVolume, toggle, next, prev, seek } = p;
  const total = duration || current.duration_sec || 0;
  const pct = total > 0 ? (progress / total) * 100 : 0;

  const tip = async () => {
    try {
      await axios.post(`${API}/music/tracks/${current.id}/tip?amount=1`);
      toast.success(`Diste $1 de propina a ${current.artist_name}`);
    } catch {
      toast.error("No se pudo enviar la propina");
    }
  };

  return (
    <div
      data-testid="player-bar"
      className="fixed bottom-0 inset-x-0 z-50 bg-ink text-cream border-t border-cream/15 backdrop-blur-xl"
    >
      {/* Progress bar */}
      <div className="h-1 bg-cream/10 cursor-pointer relative" onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        seek(ratio * total);
      }} data-testid="player-seek">
        <div className="h-full bg-coral transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-20 flex items-center gap-4">
        {/* Track info */}
        <div className="flex items-center gap-3 min-w-0 w-[280px] md:w-[320px]">
          <img src={current.cover} alt="" className="w-12 h-12 rounded-md object-cover" />
          <div className="min-w-0">
            <p data-testid="player-track-title" className="text-sm font-medium truncate">{current.title}</p>
            <p className="text-xs text-cream/60 truncate">{current.artist_name} · {current.genre}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex-1 flex flex-col items-center gap-1.5">
          <div className="flex items-center gap-2">
            <button data-testid="player-prev" onClick={prev} className="w-9 h-9 rounded-full hover:bg-cream/10 flex items-center justify-center transition">
              <SkipBack className="w-4 h-4" strokeWidth={1.8} />
            </button>
            <button
              data-testid="player-toggle"
              onClick={toggle}
              className="w-11 h-11 rounded-full bg-coral hover:bg-coral-hover text-cream flex items-center justify-center transition"
            >
              {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 translate-x-0.5" fill="currentColor" />}
            </button>
            <button data-testid="player-next" onClick={next} className="w-9 h-9 rounded-full hover:bg-cream/10 flex items-center justify-center transition">
              <SkipForward className="w-4 h-4" strokeWidth={1.8} />
            </button>
          </div>
          <div className="flex items-center gap-3 font-mono text-[10px] text-cream/55 w-full max-w-md">
            <span className="w-9 text-right">{fmtTime(progress)}</span>
            <span className="flex-1 h-px bg-cream/15" />
            <span className="w-9">{fmtTime(total)}</span>
          </div>
        </div>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3 w-[280px] md:w-[320px] justify-end">
          <button data-testid="player-tip" onClick={tip} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-cream/10 hover:bg-coral hover:text-cream transition">
            <Coins className="w-3.5 h-3.5" />
            Apoyar $1
          </button>
          <div className="hidden lg:flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-cream/55" />
            <input
              data-testid="player-volume"
              type="range" min="0" max="1" step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="accent-coral w-24"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
